import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

//
import type { NavListProps } from '../types';

// hooks
import useActiveLink from '../../../hooks/useActiveLink';
import NavItem from './NavItem';
import { StyledPopover } from './styles';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
};

const NavList = ({ data, depth, hasChild }: NavListRootProps) => {
  const navRef = useRef(null);

  const { pathname } = useLocation();

  const { active, isExternalLink } = useActiveLink(data.path);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const appBarEl = Array.from(
      document.querySelectorAll('.MuiAppBar-root')
    ) as Array<HTMLElement>;

    // Reset styles when hover
    const styles = () => {
      document.body.style.overflow = '';
      document.body.style.padding = '';
      // Apply for Window
      appBarEl.forEach((elem) => {
        elem.style.padding = '';
      });
    };

    if (open) {
      styles();
    } else {
      styles();
    }
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <NavItem
        active={active}
        depth={depth}
        isExternalLink={isExternalLink}
        item={data}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        open={open}
        ref={navRef}
      />

      {hasChild && (
        <StyledPopover
          anchorEl={navRef.current}
          anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
          open={open}
          PaperProps={{
            onMouseEnter: handleOpen,
            onMouseLeave: handleClose,
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'center' }}
        >
          <NavSubList data={data.children} depth={depth} />
        </StyledPopover>
      )}
    </>
  );
};

export default NavList;

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
};

const NavSubList = ({ data, depth }: NavListSubProps) => {
  return (
    <>
      {data.map((list) => (
        <NavList
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          key={list.title + list.path}
        />
      ))}
    </>
  );
};
